import _round from 'lodash/round';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { BudgetType } from '@/components/pages/job-posting/types';
import { durationInDays } from '@/utils/date-util';

export function useBudgetType() {
  const intl = useIntl();

  const budgetTypeOptions = [
    {
      value: 'lifetime',
      label: intl.formatMessage({
        defaultMessage: 'Total Budget',
        description: 'Budget type option for total budget',
      }),
      id: 'lifetime',
    },
    {
      value: 'daily',
      label: intl.formatMessage({
        defaultMessage: 'Per day',
        description: 'Budget type option for per day',
      }),
      id: 'daily',
    },
    {
      value: 'weekly',
      label: intl.formatMessage({
        defaultMessage: 'Per week',
        description: 'Budget type option for per week',
      }),
      id: 'weekly',
    },
    {
      value: 'monthly',
      label: intl.formatMessage({
        defaultMessage: 'Per month',
        description: 'Budget type option for per month',
      }),
      id: 'monthly',
    },
  ];

  // Helper function to calculate the total budget for the campaign based on the budget type and duration
  const calculateTotalBudget = useCallback(
    ({
      budgetType,
      budgetPerUnit,
      startDate,
      endDate,
    }: {
      budgetType: BudgetType;
      budgetPerUnit: number | null;
      startDate: string;
      endDate: string;
    }) => {
      if (!budgetPerUnit) return 0;

      const duration = Math.max(durationInDays(startDate, endDate), 0);

      switch (budgetType) {
        case 'weekly':
          return _round(duration * (budgetPerUnit / 7), 2);
        case 'monthly':
          return _round(duration * (budgetPerUnit / 30), 2);
        case 'daily':
          return _round(duration * budgetPerUnit, 2);
        case 'lifetime':
        default:
          return budgetPerUnit;
      }
    },
    [],
  );

  const getJobPostingBudgetLabel = useCallback(
    (budgetType?: BudgetType | null) => {
      switch (budgetType) {
        case 'daily':
          return intl.formatMessage({
            defaultMessage: 'Total spend per day',
          });
        case 'weekly':
          return intl.formatMessage({
            defaultMessage: 'Total spend per week',
          });
        case 'monthly':
          return intl.formatMessage({
            defaultMessage: 'Total spend per month',
          });
        case 'lifetime':
        default:
          return intl.formatMessage({
            defaultMessage: 'Total spend',
          });
      }
    },
    [intl],
  );

  const getEditCampaignBudgetLabel = useCallback(
    (budgetType?: BudgetType | null) => {
      switch (budgetType) {
        case 'daily':
          return intl.formatMessage({
            defaultMessage: 'Budget per day',
          });
        case 'weekly':
          return intl.formatMessage({
            defaultMessage: 'Budget per week',
          });
        case 'monthly':
          return intl.formatMessage({
            defaultMessage: 'Budget per month',
          });
        case 'lifetime':
        default:
          return intl.formatMessage({
            defaultMessage: 'Budget',
          });
      }
    },
    [intl],
  );

  return {
    budgetTypeOptions,
    calculateTotalBudget,
    getJobPostingBudgetLabel,
    getEditCampaignBudgetLabel,
  };
}
